'use client'

import { Button } from '@ariakit/react'
import { useSignals } from '@preact/signals-react/runtime'
import PriceInfo from '@susu/headless-commerce/components/PriceInfo/PriceInfo'
import { ResponsiveImage } from '@susu/headless-commerce/components/ResponsiveImage/ResponsiveImage'
import { generateImageSourcesForCloudinary } from '@susu/headless-commerce/components/ResponsiveImage/utils'
import { useCountry } from '@susu/headless-commerce/contexts/country/client'
import { useCurrencies } from '@susu/headless-commerce/contexts/currencies/client'
import { useLocale } from '@susu/headless-commerce/contexts/locale/client'
import styles from '@susu/headless-commerce/features/configurator/components/StepOption/StepOption.module.scss'
import type { Option } from '@susu/headless-commerce/features/configurator/domain/Option/Option'
import { configuratorSignal } from '@susu/headless-commerce/features/configurator/signals/configurator'
import { trackOptionSelect } from '@susu/headless-commerce/features/configurator/utils/trackOptionSelect'
import type { MediaWrapperV2 } from '@susu/headless-commerce/gql/generated/graphql'
import { usePageType } from '@susu/headless-commerce/hooks/usePageType'
import log from '@susu/headless-commerce/utils/log'
import { isNotNull } from '@susu/null'
import { isNotUndefined } from '@susu/undefined'
import classNames from 'classnames'
import Image from 'next/image'
import { useRouter } from 'next/navigation'
import { useCallback, useMemo } from 'react'

const sizes = {
  xs: '60vw',
  lg: '15vw',
}

export type ClientStepOptionProps = {
  count: number
  id: string | undefined
  name: string
  option: Option
  isOutOfStock: boolean
  recommendedIndication: boolean
  thumbnailImage: MediaWrapperV2 | undefined
}

export const ClientStepOption = ({
  count,
  id,
  name,
  option,
  isOutOfStock,
  recommendedIndication,
  thumbnailImage,
}: Readonly<ClientStepOptionProps>) => {
  useSignals()

  const router = useRouter()
  const country = useCountry()
  const locale = useLocale()
  const currencies = useCurrencies()
  const configurator = configuratorSignal.value
  const isSelected = configurator.selection.value.isOptionSelected(option)
  const price = option.getSelectedCampaignPriceDifference()
  const pageType = usePageType()

  const select = useCallback(async () => {
    try {
      await configurator.selectOption(option)

      const ids = configurator.selection.value.encodeToQueryString(
        configurator.selection.value.current.value,
      )
      const url = new URL(window.location.toString())
      url.searchParams.set('ids', ids)
      const urlAsString = decodeURIComponent(url.toString())
      window.history.pushState({}, '', urlAsString)

      await trackOptionSelect({
        configurator,
        country,
        locale,
        pageType,
        option,
      })
    } catch (error: unknown) {
      log.error(error)
      router.push('/')
    }
  }, [country, configurator, option, pageType, router.push, locale])

  const images = useMemo(
    () => ({
      cloudinaryDesktopImage: thumbnailImage?.cloudinaryDesktopImage,
      cloudinaryTabletImage: thumbnailImage?.cloudinaryTabletImage,
      cloudinaryMobileImage: thumbnailImage?.cloudinaryMobileImage,
    }),
    [
      thumbnailImage?.cloudinaryDesktopImage,
      thumbnailImage?.cloudinaryMobileImage,
      thumbnailImage?.cloudinaryTabletImage,
    ],
  )

  const {
    desktopSrcSet,
    mobileSrcSet,
    tabletSrcSet,
    desktopSizes,
    mobileSizes,
    tabletSizes,
    smallestUrl,
  } = useMemo(() => generateImageSourcesForCloudinary(images, sizes), [images])

  return (
    <Button
      className={classNames(styles.option, {
        [styles.option__selected]: isSelected,
        [styles.option__half]: count === 2,
        [styles.option__oos]: isOutOfStock,
      })}
      onClick={select}
      disabled={isOutOfStock}
      data-id={id}
      data-testid={`configurator_option_${id ?? 'no-id'}`}
    >
      {isNotUndefined(price) && (
        <div
          className={classNames(styles.option__price, 'caption-regular')}
          data-testid="option_extra-price"
        >
          <PriceInfo price={price} country={country} currencies={currencies} />
        </div>
      )}
      {isOutOfStock && (
        <div
          className={classNames(styles['option__oos-badge'], 'caption-regular')}
          data-testid="option_out-of-stock"
        >
          Out of stock
        </div>
      )}
      <div className={styles.option__image}>
        {isNotNull(thumbnailImage) ? (
          <ResponsiveImage
            alt={name}
            desktopSizes={desktopSizes}
            desktopSrcSet={desktopSrcSet}
            imgStyle={{ width: '100%', height: '100%' }}
            lazyloading={true}
            mobileSizes={mobileSizes}
            mobileSrcSet={mobileSrcSet}
            pictureStyle={{ display: 'flex' }}
            src={smallestUrl}
            tabletSizes={tabletSizes}
            tabletSrcSet={tabletSrcSet}
          />
        ) : (
          <Image
            src={
              'https://cdn.suitsupply.com/image/upload/b_rgb:ffffff,c_fit,f_auto,h_800,q_auto:good,w_800/custommade/thumbs2/Jacket/MBN1'
            }
            width="800"
            height="800"
            alt={'Placeholder - ${name}'}
            loading="lazy"
          />
        )}
      </div>

      <div className={classNames(styles.option__name)}>
        {!isOutOfStock && recommendedIndication === true && (
          <div className={styles['option__name-image']}>
            <Image
              src="https://cdn.suitsupply.com/image/upload/svg/recommended.svg"
              width="20"
              height="20"
              alt="recommended indicator"
              loading="lazy"
            />
          </div>
        )}
        <div
          className={classNames(
            styles['option__name-text'],
            `caption-${isSelected ? 'medium' : 'light'}-sm`,
            `body-${isSelected ? 'medium' : 'light'}-lg`,
            {
              [styles['option__name-text-recommended']]:
                recommendedIndication === true,
            },
          )}
        >
          {name}
        </div>
      </div>
    </Button>
  )
}
